import React from 'react';
import styled, { keyframes } from 'styled-components';

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const spin = keyframes`
  from { transform: rotate(0deg) scale(1); }
  50% { transform: rotate(180deg) scale(1.2); }
  to { transform: rotate(360deg) scale(1); }
`;

const Section = styled.section`
  max-width: 1200px;
  margin: 0 auto;
  padding: 5rem 2rem;
  animation: fadeIn 0.8s ease;

  @media (max-width: 768px) {
    padding: 3rem 1rem;
  }
`;

const Title = styled.h2`
  font-size: 2.5rem;
  color: #2e7d32;
  margin-bottom: 2rem;
  text-align: center;
  position: relative;
  display: inline-block;
  width: 100%;

  &::after {
    content: '';
    position: absolute;
    bottom: -10px;
    left: 50%;
    transform: translateX(-50%);
    width: 80px;
    height: 4px;
    background: linear-gradient(to right, #4CAF50, #ff9800);
    border-radius: 2px;
    animation: expandWidth 1s ease;
  }

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const Subtitle = styled.p`
  text-align: center;
  margin-bottom: 2rem;
  font-size: 1.1rem;
`;

const PillarsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  margin-top: 3rem;
`;

const PillarCard = styled.div`
  background: white;
  padding: 2rem;
  border-radius: 15px;
  box-shadow: 0 5px 20px rgba(0,0,0,0.08);
  transition: transform 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275), box-shadow 0.4s;
  position: relative;
  overflow: hidden;
  animation: ${fadeInUp} 0.6s ease forwards;
  opacity: 0;

  &:nth-child(1) { animation-delay: 0.1s; }
  &:nth-child(2) { animation-delay: 0.2s; }
  &:nth-child(3) { animation-delay: 0.3s; }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 5px;
    background: linear-gradient(to right, #4CAF50, #2e7d32);
    transform: scaleX(0);
    transition: transform 0.4s ease;
  }

  &:hover::before {
    transform: scaleX(1);
  }

  &:hover {
    transform: translateY(-15px) scale(1.02);
    box-shadow: 0 20px 50px rgba(46, 125, 50, 0.2);
  }
`;

const PillarIcon = styled.div`
  font-size: 3.5rem;
  margin-bottom: 1rem;
  display: inline-block;
  animation: bounce 2s ease-in-out infinite;
`;

const PillarCardHover = styled.div`
  &:hover ${PillarIcon} {
    animation: ${spin} 0.6s ease;
  }
`;

const PillarTitle = styled.h3`
  color: #2e7d32;
  margin-bottom: 1rem;
  font-size: 1.5rem;
`;

const PillarList = styled.ul`
  margin-top: 1rem;
  line-height: 1.8;
  list-style: none;
  padding: 0;
`;

const PillarItem = styled.li`
  padding-left: 1rem;
  position: relative;

  &::before {
    content: '✓';
    position: absolute;
    left: 0;
    color: #4CAF50;
    font-weight: bold;
  }
`;

const Pillars: React.FC = () => {
  return (
    <Section id="pillars">
      <Title>Our Three Pillars</Title>
      <Subtitle>
        Unlike traditional NGOs that focus on single sectors, we integrate health, education, and agriculture to create lasting, synergistic impact.
      </Subtitle>

      <PillarsGrid>
        <PillarCardHover>
          <PillarCard>
            <PillarIcon>🏥</PillarIcon>
            <PillarTitle>Public Health</PillarTitle>
            <p><strong>The Foundation</strong></p>
            <PillarList>
              <PillarItem>Infectious disease prevention (malaria, cholera, TB, HIV)</PillarItem>
              <PillarItem>Maternal and child health</PillarItem>
              <PillarItem>WASH (water, sanitation, hygiene)</PillarItem>
              <PillarList>Nutrition-linked health education</PillarList>
              <PillarItem>Zoonotic disease control</PillarItem>
            </PillarList>
          </PillarCard>
        </PillarCardHover>

        <PillarCardHover>
          <PillarCard>
            <PillarIcon>📚</PillarIcon>
            <PillarTitle>Education</PillarTitle>
            <p><strong>The Catalyst</strong></p>
            <PillarList>
              <PillarItem>Functional literacy with applied knowledge</PillarItem>
              <PillarItem>Adult and community education</PillarItem>
              <PillarItem>Health literacy integrated into curricula</PillarItem>
              <PillarItem>Life skills and financial literacy</PillarItem>
              <PillarItem>School feeding programs from local farms</PillarItem>
            </PillarList>
          </PillarCard>
        </PillarCardHover>

        <PillarCardHover>
          <PillarCard>
            <PillarIcon>🌾</PillarIcon>
            <PillarTitle>Agriculture</PillarTitle>
            <p><strong>The Sustainer</strong></p>
            <PillarList>
              <PillarItem>Climate-smart agriculture</PillarItem>
              <PillarItem>Nutrition-sensitive farming</PillarItem>
              <PillarItem>Kitchen gardens and crop diversification</PillarItem>
              <PillarItem>Agricultural value chains</PillarItem>
              <PillarItem>Market access and food processing</PillarItem>
            </PillarList>
          </PillarCard>
        </PillarCardHover>
      </PillarsGrid>
    </Section>
  );
};

export default Pillars;